import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Search, Zap, LogOut, Shield, Menu, X, Bookmark } from "lucide-react";
import { getUser, clearAuth } from "../lib/auth";

export default function Nav() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setUser(getUser());
  }, [location]);

  const logout = () => {
    clearAuth();
    setUser(null);
    navigate("/");
  };

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/articles?search=${encodeURIComponent(q)}`);
  };

  const linkCls = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
      isActive ? "text-cyan1" : "text-slate-300 hover:text-cyan1"
    }`;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-void/80 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan1 to-magenta1 flex items-center justify-center shadow-cyanglow">
            <Zap className="w-5 h-5 text-void" strokeWidth={3} />
          </div>
          <div className="leading-none">
            <div className="font-heading text-xl font-black tracking-tight text-white">
              NOX<span className="gradient-text">AI</span>
            </div>
            <div className="font-mono text-[9px] tracking-[0.3em] text-slate-500 uppercase">
              AI · Robotics · Agents
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Beranda</NavLink>
          <NavLink to="/articles" className={linkCls} data-testid="nav-articles">Berita</NavLink>
          <NavLink to="/repositories" className={linkCls} data-testid="nav-repositories">Agent Catalog</NavLink>
          <NavLink to="/videos" className={linkCls} data-testid="nav-videos">Video</NavLink>
          <NavLink to="/newsletter" className={linkCls} data-testid="nav-newsletter">Newsletter</NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about">About</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <form onSubmit={submit} className="hidden lg:flex items-center bg-card border border-white/10 rounded-full px-3 h-9 focus-within:border-cyan1/50 transition">
            <Search className="w-4 h-4 text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari berita AI..."
              className="bg-transparent outline-none px-2 text-sm w-36 xl:w-52 placeholder:text-slate-600"
              data-testid="global-search-input"
            />
          </form>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/bookmarks" className="btn-ghost !px-3" title="Bookmark saya" data-testid="nav-bookmarks">
                <Bookmark className="w-4 h-4" />
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" className="btn-ghost" data-testid="nav-admin">
                  <Shield className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link to="/profile" className="flex items-center gap-2 pl-2 border-l border-white/10 hover:opacity-80 transition" data-testid="nav-user">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan1/50 to-magenta1/50 flex items-center justify-center text-xs font-bold font-heading">
                    {(user.name || user.email || "?")[0].toUpperCase()}
                  </div>
                )}
                <span className="text-sm text-slate-300 max-w-[110px] truncate">{user.name || user.email}</span>
              </Link>
              <button onClick={logout} className="btn-ghost !px-3" title="Keluar" data-testid="nav-logout">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link to="/login" className="btn-ghost hidden md:inline-flex" data-testid="nav-login">
              <Shield className="w-4 h-4" /> Masuk
            </Link>
          )}

          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-void/95 border-t border-white/10 px-4 py-4 space-y-3">
          <NavLink to="/" end className="block text-slate-200" onClick={() => setOpen(false)} data-testid="mobile-nav-home">Beranda</NavLink>
          <NavLink to="/articles" className="block text-slate-200" onClick={() => setOpen(false)} data-testid="mobile-nav-articles">Berita</NavLink>
          <NavLink to="/repositories" className="block text-slate-200" onClick={() => setOpen(false)} data-testid="mobile-nav-repositories">Agent Catalog</NavLink>
          <NavLink to="/videos" className="block text-slate-200" onClick={() => setOpen(false)} data-testid="mobile-nav-videos">Video</NavLink>
          <NavLink to="/newsletter" className="block text-slate-200" onClick={() => setOpen(false)} data-testid="mobile-nav-newsletter">Newsletter</NavLink>
          <NavLink to="/about" className="block text-slate-200" onClick={() => setOpen(false)} data-testid="mobile-nav-about">About</NavLink>
          {user ? (
            <>
              <Link to="/bookmarks" className="text-slate-200 flex items-center gap-2" onClick={() => setOpen(false)} data-testid="mobile-nav-bookmarks"><Bookmark className="w-3 h-3" /> Bookmark Saya</Link>
              <Link to="/profile" className="block text-slate-200" onClick={() => setOpen(false)} data-testid="mobile-nav-profile">Profil Saya</Link>
              {user.role === "admin" && (
                <Link to="/admin" className="block text-cyan1" onClick={() => setOpen(false)} data-testid="mobile-nav-admin">Admin Dashboard</Link>
              )}
              <button onClick={logout} className="block text-magenta1" data-testid="mobile-nav-logout">Keluar ({user.name || user.email})</button>
            </>
          ) : (
            <Link to="/login" className="block text-cyan1" onClick={() => setOpen(false)} data-testid="mobile-nav-login">Masuk / Daftar</Link>
          )}
        </div>
      )}
    </header>
  );
}
